import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'antd';
import {
    RollbackOutlined,
    CopyOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    DownOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const MessageBubbleActions = ({
    message,
    hasMedia,
    canReplyToThis,
    onAction,
    children,
    isHovered,
    contextMenuOpen,
    setContextMenuOpen,
    isMobile,
    isViewOnly = false
}) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [mobileAnchor, setMobileAnchor] = useState({ x: 0, y: 0 });
    const dropdownOverlayStyle = isMobile
        ? { width: 180, minWidth: 180 }
        : undefined;

    const menuItems = [{
        key: 'reply',
        label: t('reply'),
        icon: <RollbackOutlined />,
        disabled: !canReplyToThis,
        onClick: () => onAction('reply')
    }, {
        key: 'copy',
        label: t('copy'),
        icon: <CopyOutlined />,
        disabled: !message?.content,
        onClick: () => onAction('copy')
    }, {
        key: 'download',
        label: t('download'),
        icon: <DownloadOutlined />,
        disabled: !hasMedia,
        onClick: () => onAction('download')
    }, {
        key: 'details',
        label: t('details'),
        icon: <InfoCircleOutlined />,
        onClick: () => onAction('details')
    }];

    const shouldIgnoreMobileTap = (target) => {
        if (!target?.closest) return false;
        return Boolean(target.closest('a, button, input, textarea, select, video, audio, [role="button"]'));
    };

    const handleMobileTap = (event) => {
        if (!isMobile || shouldIgnoreMobileTap(event.target)) return;

        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
            setMobileAnchor({
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
            });
        }
        setContextMenuOpen(true);
    };

    useEffect(() => {
        if (!isMobile || !contextMenuOpen) return undefined;

        const handleOutsideClick = (event) => {
            const target = event.target;
            const clickedInsideBubble = containerRef.current?.contains(target);
            const clickedInsideDropdown = target?.closest?.('.ant-dropdown');

            if (!clickedInsideBubble && !clickedInsideDropdown) {
                setContextMenuOpen(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [contextMenuOpen, isMobile, setContextMenuOpen]);

    if (isViewOnly) {
        return children;
    }

    return (
        <div
            ref={containerRef}
            onClick={handleMobileTap}
            style={{ position: 'relative' }}
        >
            <Dropdown
                menu={{ items: menuItems }}
                trigger={isMobile ? [] : ['contextMenu']}
                overlayStyle={dropdownOverlayStyle}
            >
                {children}
            </Dropdown>

            {isMobile && contextMenuOpen && (
                <div
                    style={{
                        position: 'absolute',
                        left: mobileAnchor.x,
                        top: mobileAnchor.y,
                        width: 1,
                        height: 1,
                        pointerEvents: 'none',
                    }}
                >
                    <Dropdown
                        menu={{ items: menuItems }}
                        trigger={[]}
                        open={contextMenuOpen}
                        overlayStyle={dropdownOverlayStyle}
                        onOpenChange={(open) => {
                            setContextMenuOpen(open);
                        }}
                        placement="bottomLeft"
                    >
                        <span style={{ display: 'block', width: 1, height: 1 }} />
                    </Dropdown>
                </div>
            )}

            {(isHovered || contextMenuOpen) && !isMobile && (
                <div style={{ position: 'absolute', top: 5, right: 5, zIndex: 10 }}>
                    <Dropdown
                        menu={{ items: menuItems }}
                        trigger={['click']}
                        open={contextMenuOpen}
                        overlayStyle={dropdownOverlayStyle}
                        onOpenChange={(open) => {
                            setContextMenuOpen(open);
                        }}
                        placement="bottomRight"
                    >
                        <div style={{
                            cursor: 'pointer',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: 'rgba(0, 0, 0, 0.4)',
                            backdropFilter: 'blur(2px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                            <DownOutlined style={{ color: '#fff', fontSize: 10 }} />
                        </div>
                    </Dropdown>
                </div>
            )}
        </div>
    );
};

export default MessageBubbleActions;
